import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { formatApiError } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export function AdminLogin() {
    const { user, login, ready } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { document.title = "Admin · Final Cut Technologies"; }, []);
    if (ready && user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            navigate("/admin");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setSubmitting(false); }
    };

    return (
        <div className="min-h-screen bg-bg text-ink flex items-center justify-center px-6">
            <Toaster position="top-right" />
            <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md border border-line bg-bg p-10">
                <div className="flex items-center gap-3 mb-8">
                    <span className="w-11 h-11 grid place-items-center bg-ink overflow-hidden">
                        <img src="/brand/logo-fct.png" alt="Final Cut Technologies" className="w-full h-full object-contain" />
                    </span>
                    <div>
                        <p className="font-display text-base tracking-tight leading-none">Final Cut Technologies</p>
                        <p className="tick text-muted mt-1">Private lead dashboard</p>
                    </div>
                </div>
                <p className="eyebrow mb-2 text-gold">Admin</p>
                <h1 className="font-display text-3xl tracking-tight mb-6">Sign in.</h1>
                <div className="space-y-4">
                    <div>
                        <label className="eyebrow block mb-2">Email</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" required
                            className="w-full border border-line bg-bg px-4 py-3 text-sm focus:border-ink outline-none" />
                    </div>
                    <div>
                        <label className="eyebrow block mb-2">Password</label>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" required
                            className="w-full border border-line bg-bg px-4 py-3 text-sm focus:border-ink outline-none" />
                    </div>
                </div>
                <button type="submit" disabled={submitting} data-testid="admin-login-submit"
                    className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-ink text-bg py-3 text-sm hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
                    {submitting ? "Signing in…" : "Sign in"} <ArrowUpRight className="w-4 h-4" />
                </button>
            </form>
        </div>
    );
}

export function ProtectedAdmin({ children }) {
    const { user, ready } = useAuth();
    if (!ready) return <div className="min-h-screen bg-bg" />;
    if (!user) return <Navigate to="/admin/login" replace />;
    return children;
}
